import {
  Button,
  Upload,
  Space,
  Flex,
  message,
  Typography,
  UploadProps,
} from "antd";
import {
  FilePdfOutlined,
  DeleteOutlined,
  UploadOutlined,
} from "@ant-design/icons";
import { useMutation } from "@tanstack/react-query";
import { useRef, useState } from "react";
import { uploadMedia } from "../../lib/api/resources/media";
import { Media } from "@rumble-it/common-lib/types/models/Media.interface";

interface SingleDocumentUploadProps {
  multiple?: false;
  value?: Media;
  onChange?: (value: Media | null) => void;
}

interface MultipleDocumentUploadProps {
  multiple: true;
  value?: Media[];
  onChange?: (value: Media[]) => void;
}

type DocumentUploadProps =
  | SingleDocumentUploadProps
  | MultipleDocumentUploadProps;

export const DocumentUpload = (props: DocumentUploadProps) => {
  const [uploadingCount, setUploadingCount] = useState(0);

  const documents: Media[] = props.multiple
    ? (props.value ?? [])
    : props.value
      ? [props.value]
      : [];

  const documentsRef = useRef(documents);
  documentsRef.current = documents;

  const uploadMutation = useMutation({
    mutationFn: ({
      file,
      onProgress,
    }: {
      file: File;
      onProgress?: (percent: number) => void;
    }) => {
      return uploadMedia(file, onProgress);
    },
  });

  const handleRemove = (index: number) => {
    if (props.multiple) {
      props.onChange?.(documents.filter((_: Media, i: number) => i !== index));
    } else {
      props.onChange?.(null);
    }
  };

  const handleUpload: UploadProps["customRequest"] = async (options) => {
    setUploadingCount((c) => c + 1);

    try {
      const doc = await uploadMutation.mutateAsync({
        file: options.file as File,
        onProgress: (percent) => {
          options.onProgress?.({ percent });
        },
      });

      if (props.multiple) {
        props.onChange?.([...documentsRef.current, doc]);
      } else {
        props.onChange?.(doc);
      }
      options.onSuccess?.(doc, options.file);
    } catch {
      message.error("Er is een fout opgetreden bij het uploaden");
      options.onError?.(new Error("Upload mislukt"));
    } finally {
      setUploadingCount((c) => c - 1);
    }
  };

  return (
    <Flex vertical gap="small" style={{ width: "100%" }}>
      {documents.length > 0 ? (
        <Flex vertical style={{ marginBottom: 15 }}>
          {documents.map((doc, index) => (
            <Flex
              key={doc.id ?? index}
              justify="space-between"
              align="center"
              style={{ borderBottom: "1px solid #eee", padding: "12px 0" }}
            >
              <Space style={{ alignItems: "start", gap: 15 }}>
                <FilePdfOutlined
                  className="text-primary-500"
                  style={{ fontSize: 18 }}
                />
                <Space orientation="vertical" style={{ gap: 2 }}>
                  <Typography.Link
                    href={doc.contentUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {doc.originalName ?? "Document"}
                  </Typography.Link>
                  {doc.size ? (
                    <Typography.Text style={{ color: "#bbb", fontSize: 12 }}>
                      {(doc.size / (1024 * 1024)).toFixed(2)} MB
                    </Typography.Text>
                  ) : null}
                </Space>
              </Space>
              <Button
                type="text"
                danger
                icon={<DeleteOutlined />}
                onClick={() => handleRemove(index)}
              />
            </Flex>
          ))}
        </Flex>
      ) : null}
      <Upload
        accept=".pdf"
        multiple={props.multiple}
        showUploadList={false}
        customRequest={handleUpload}
      >
        <Button loading={uploadingCount > 0} icon={<UploadOutlined />}>
          {props.multiple
            ? "Documenten toevoegen"
            : documents.length > 0
              ? "Ander document kiezen"
              : "Document kiezen"}
        </Button>
      </Upload>
    </Flex>
  );
};
